import React, {useContext, useEffect, useState, useMemo, useRef} from 'react';
import {List} from 'immutable';
import classnames from 'classnames';
import {
  Anchor,
  Checkbox,
  Label,
  Text,
  Toggle,
  Icon,
  IconButton,
  Button,
  WithTooltip
} from '@albert-io/atomic';

import SubjectAndGuideSelector from 'client/components/ContentDiscovery/SubjectAndGuideSelector/SubjectAndGuideSelector.react';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';

import {
  Difficulties,
  QuestionTypes,
  constructAllOfFilter,
  constructQuestionTypesFilter,
  constructStandardsStandardSetsFilter,
  DIFFICULTIES,
  QUESTION_TYPES as QUESTION_TYPE_NAMES,
  numToDifficultyMap
} from 'client/components/ContentDiscovery/ContentDiscovery.utils';
import StandardSelector from 'client/components/ContentDiscovery/StandardSelector/StandardSelector.react';
import {useTracking} from 'react-tracking';
import sessionStore from 'client/Session/SessionStore';
import {QUESTION_TYPES} from 'client/constants';

import {SubjectChipGroup, StandardSetChipGroup} from './FilterBlocks';

import {
  getBaseQuestionDifficultyCountQuery,
  getBaseQuestionTypeCountQuery
} from './FilterGroup.queries';

import './filter-group.scss';

export const SideBarSlider = () => {
  const {
    hasFilters,
    questionsQuery,
    sidebarCollapsed: collapsed,
    setSidebarCollapsed: setCollapsed
  } = useContext(ContentDiscoveryContext);

  const {Track} = useTracking({
    component: 'filters panel'
  });
  const [response, setResponse] = useState<null | any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const executeQuery = async () => {
      if (!questionsQuery || !hasFilters) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const response = await questionsQuery.getResourcePromise();
      const resultCount = await questionsQuery
        .getResourceMetadata()
        .getIn(['page', 'total_entries'], 0);

      setResponse(response);
      setTotalEntries(resultCount);
      setIsLoading(false);
    };
    executeQuery();
  }, [hasFilters, questionsQuery]);

  useEffect(() => {
    if (hasFilters) {
      setShowError(false);
    }
  }, [hasFilters]);

  return (
    <Track>
      <div className='u-display_flex'>
        <div className='add-questions__sidebar-nav'>
          <WithTooltip content={collapsed ? 'Show Filters' : 'Hide filters'} placement='right'>
            <IconButton
              color='primary'
              variant={collapsed ? 'solid' : 'outlined'}
              label={collapsed ? 'Show Filters' : 'Show Results'}
              icon={collapsed ? 'bars-filter' : 'times'}
              disabled={!collapsed && !hasFilters}
              onClick={() => setCollapsed((c) => !c)}
            />
          </WithTooltip>
        </div>
      </div>
      <FilterGroup
        isLoading={isLoading}
        response={response}
        totalEntries={totalEntries}
        showError={showError}
        isCollapsed={collapsed}
        onClose={() => setCollapsed(true)}
      />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={classnames({'filter-overlay--collapsed': collapsed}, 'filter-overlay')}
        onClick={() => {
          if (!hasFilters) {
            setShowError(true);
          } else if (!isLoading && response && !response.isEmpty()) {
            setCollapsed(true);
          }
        }}
      >
        {hasFilters && response !== null && response.isEmpty() && (
          <div className='filter-overlay__message-container'>
            <Icon size='standalone' color='inherit' icon={['fal', 'search']} />
            <Text size='m' bold color='inherit'>
              No questions found
            </Text>
            <Text size='m' color='inherit'>
              Try adjusting your filters.
            </Text>
          </div>
        )}
      </div>
    </Track>
  );
};

interface FilterGroupProps {
  isCollapsed: boolean;
  onClose: () => void;
  response: any | null;
  isLoading: boolean;
  showError: boolean;
  totalEntries: number;
}

const FilterGroup = ({
  isCollapsed,
  onClose,
  response,
  isLoading,
  totalEntries,
  showError
}: FilterGroupProps) => {
  const {clearFilters, hasFilters} = useContext(ContentDiscoveryContext);

  const {trackEvent} = useTracking({event: 'click', type: 'button'});

  const buttonText = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <Icon icon='spinner' spin className='u-mar-r_2' />
          Finding Questions...
        </>
      );
    }
    if (!hasFilters || response === null) {
      return 'Show Results';
    }
    if (response.isEmpty()) {
      return 'Show 0 Results';
    }
    return `Show ${totalEntries} Results`;
  }, [response, isLoading, totalEntries, hasFilters]);

  return (
    <div
      className={classnames(
        {'add-questions__filter-sidebar---collapsed': isCollapsed},
        'add-questions__filter-sidebar'
      )}
    >
      <header className='assignment-section-header__title u-pad-lr_3 u-pad-tb_1 u-display_flex'>
        Filters
        {hasFilters && (
          <Anchor
            tabIndex={0}
            className='a-text--size-s u-mar-l_auto u-font-weight_normal'
            onClick={() => {
              clearFilters();
              trackEvent({content: 'clear all'});
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                clearFilters();
                trackEvent({content: 'clear all'});
              }
            }}
          >
            Clear all
          </Anchor>
        )}
      </header>
      <div className='u-display_flex u-flex-direction_column u-flex-grow_1 u-pad-b_12 u-overflow-y_auto u-position_relative'>
        <div className='filter-container'>
          <StandardSelector />
          <StandardSetChipGroup />
        </div>
        <div className='filter-container'>
          <SubjectAndGuideSelector />
          <SubjectChipGroup />
        </div>
        <HideAssessmentQuestions />
        <HidePreviouslyAssigned />
        <DifficultyFilters />
        <QuestionTypeFilters />
      </div>
      <div className='add-questions__sidebar-bottom-bar'>
        {showError && (
          <Text as='p' bold className=' u-mar-tb_0 u-mar-r_2' color='red'>
            You&apos;ll need to add filters first.
          </Text>
        )}
        <Button
          data-test-id='AB-sidebar__show-results-btn'
          disabled={!hasFilters || response === null || response.isEmpty() || isLoading}
          onClick={onClose}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

type DifficultyCounts = {
  [K in Difficulties]: number;
};

const DifficultyFilters = () => {
  const {questionDifficulties, setQuestionDifficulties, questionsQuery, hasFilters} =
    useContext(ContentDiscoveryContext);

  const prevQuestionDifficulties = useRef<List<Difficulties>>(List());

  const {trackEvent} = useTracking({event: 'click', type: 'checkbox'});

  const initialDifficultyCounts = DIFFICULTIES.reduce((acc, difficulty) => {
    acc[difficulty] = 0;
    return acc;
  }, {} as DifficultyCounts);

  const [difficultyCounts, setDifficultyCounts] = useState(initialDifficultyCounts);
  const [countsLoaded, setCountsLoaded] = useState(false);

  const loadInitialQuestionCounts = async () => {
    const countsQuery = getBaseQuestionDifficultyCountQuery();

    const response = await countsQuery.getResourcePromise();

    // This is an unfiltered query over question_sets_v1 so we're guaranteed to get results back.
    const result = response.first().getMeta().getQuestionDifficultyCounts();

    const newCounts = {} as DifficultyCounts;

    for (const [difficultyNum, difficulty] of Object.entries(numToDifficultyMap)) {
      newCounts[difficulty] = result.get(difficultyNum);
    }

    setDifficultyCounts(newCounts);

    setCountsLoaded(true);
  };

  useEffect(() => {
    loadInitialQuestionCounts();
  }, []);

  const refreshQuestionCounts = async () => {
    setCountsLoaded(false);

    const baseQuery = questionsQuery.unset('filter.questions_v3.difficulty');

    const countsQuery = baseQuery
      .meta({
        context: {
          question_difficulty_counts: true
        }
      })
      .withMeta('question_set_v1');

    const response = await countsQuery.getResourcePromise();

    const result = response.first()?.getMeta().getQuestionDifficultyCounts();

    if (result) {
      const newCounts = {} as DifficultyCounts;

      for (const [difficultyNum, difficulty] of Object.entries(numToDifficultyMap)) {
        newCounts[difficulty] = result.get(difficultyNum);
      }

      setDifficultyCounts(newCounts);
    }

    setCountsLoaded(true);
  };

  const refreshQuestionCountsWithSideEffects = async () => {
    // Counts are not affected by filters within the same category so we should do nothing if
    // it turns out the useEffect was called because the questionDifficulties filter was modified.
    if (questionDifficulties.equals(prevQuestionDifficulties.current)) {
      if (questionsQuery && hasFilters) {
        await refreshQuestionCounts();
      } else {
        await loadInitialQuestionCounts();
      }
    }
    prevQuestionDifficulties.current = questionDifficulties;
  };

  useEffect(() => {
    if (!sessionStore.isSuper()) {
      return;
    }
    refreshQuestionCountsWithSideEffects();
  }, [questionsQuery, hasFilters]);

  const toggleDifficulty = (difficulty) => {
    let newDifficulties = questionDifficulties;
    if (questionDifficulties.includes(difficulty)) {
      //  @ts-ignore - immutable filter
      newDifficulties = questionDifficulties.filter((diff) => diff !== difficulty);
    } else {
      newDifficulties = questionDifficulties.push(difficulty);
    }
    setQuestionDifficulties(newDifficulties);
    trackEvent({content: difficulty});
  };

  return (
    <div className='filter-container u-flex-direction_column'>
      <div className='filter-label'>
        <Text size='s' className='u-flex-grow_1 u-mar-b_1'>
          Question Difficulty
        </Text>
      </div>
      <div className='filter-options'>
        {DIFFICULTIES.map((difficulty) => {
          const difficultyCount = difficultyCounts[difficulty];
          const fadeText = sessionStore.isSuper() && countsLoaded && !difficultyCount;
          return (
            <Label className='u-mar-t_1 u-text-transform_capitalize'>
              <Checkbox
                size='s'
                className='u-mar-r_2'
                onChange={() => toggleDifficulty(difficulty)}
                aria-hidden={false}
                // @ts-ignore - TODO: fix
                checked={questionDifficulties.includes(difficulty)}
              />
              <Text size='s' emphasis={fadeText ? 'lowest' : undefined}>
                {difficulty}
              </Text>
              {sessionStore.isSuper() && countsLoaded && (
                <Text size='xs' emphasis={fadeText ? 'disabled' : 'lowest'}>
                  &nbsp;&nbsp;({difficultyCount})
                </Text>
              )}
            </Label>
          );
        })}
      </div>
    </div>
  );
};

type TypeCounts = {
  [K in QuestionTypes]: number;
};

const QuestionTypeFilters = () => {
  const {
    questionTypes,
    setQuestionTypes,
    questionsQuery,
    hasFilters,
    getSelectedStandardsAndSets,
    standardCollection
  } = useContext(ContentDiscoveryContext);

  const prevQuestionTypes = useRef<List<QuestionTypes>>(List());

  const {trackEvent} = useTracking({event: 'click', type: 'checkbox'});

  const initialTypeCounts = QUESTION_TYPE_NAMES.reduce((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {} as TypeCounts);

  const [typeCounts, setTypeCounts] = useState(initialTypeCounts);
  const [countsLoaded, setCountsLoaded] = useState(false);

  const loadInitialQuestionCounts = async () => {
    const countsQuery = getBaseQuestionTypeCountQuery();

    const response = await countsQuery.getResourcePromise();

    // This is an unfiltered query over question_sets_v1 so we're guaranteed to get results back.
    const result = response.first()?.getMeta().getQuestionTypeCounts();

    const newCounts = {} as TypeCounts;
    QUESTION_TYPE_NAMES.forEach((questionType) => {
      newCounts[questionType] = result.get(questionType);
    });
    setTypeCounts(newCounts);

    setCountsLoaded(true);
  };

  useEffect(() => {
    loadInitialQuestionCounts();
  }, []);

  const refreshQuestionCounts = async () => {
    setCountsLoaded(false);

    const baseQuery = questionsQuery.unset('filter.all_of');

    const {selectedStandardIds, selectedStandardSetIds} =
      getSelectedStandardsAndSets(standardCollection);

    const [standardSetsFilter, standardsFilter] = constructStandardsStandardSetsFilter(
      selectedStandardIds,
      selectedStandardSetIds
    );

    const questionTypesFilter = constructQuestionTypesFilter(List());

    const allOfFilter = constructAllOfFilter(
      questionTypesFilter,
      standardsFilter,
      standardSetsFilter
    );

    const countsQuery = baseQuery
      .filter(allOfFilter)
      .meta({
        context: {
          question_type_counts: true
        }
      })
      .withMeta('question_set_v1');

    const response = await countsQuery.getResourcePromise();

    const result = response.first()?.getMeta().getQuestionTypeCounts();

    if (result) {
      const newCounts = {} as TypeCounts;

      QUESTION_TYPE_NAMES.forEach((questionType) => {
        newCounts[questionType] = result.get(questionType);
      });

      setTypeCounts(newCounts);
    }

    setCountsLoaded(true);
  };

  const refreshQuestionCountsWithSideEffects = async () => {
    // Counts are not affected by filters within the same category so we should do nothing if
    // it turns out the useEffect was called because the questionType filter was modified.
    if (questionTypes.equals(prevQuestionTypes.current)) {
      if (questionsQuery && hasFilters) {
        await refreshQuestionCounts();
      } else {
        await loadInitialQuestionCounts();
      }
    }
    prevQuestionTypes.current = questionTypes;
  };

  useEffect(() => {
    if (!sessionStore.isSuper()) {
      return;
    }

    refreshQuestionCountsWithSideEffects();
  }, [questionsQuery, hasFilters]);

  const toggleQuestionType = (questionType) => {
    let newQuestionTypes = questionTypes;

    if (questionTypes.includes(questionType)) {
      newQuestionTypes = questionTypes.filter((type) => type !== questionType).toList();
    } else {
      newQuestionTypes = questionTypes.push(questionType);
    }

    setQuestionTypes(newQuestionTypes);
    trackEvent({content: questionType});
  };

  return (
    <div className='filter-container u-flex-direction_column'>
      <div className='filter-label'>
        <Text size='s' className='u-flex-grow_1 u-mar-b_1'>
          Question Type
        </Text>
      </div>
      <div className='filter-options'>
        {Object.values(QUESTION_TYPES).map((questionType) => {
          // @ts-ignore TODO: type questionType
          const {name, title} = questionType;
          const typeCount = typeCounts[name];
          const fadeText = sessionStore.isSuper() && countsLoaded && !typeCount;
          return (
            <Label className='u-mar-t_1'>
              <Checkbox
                size='s'
                className='u-mar-r_2'
                onChange={() => {
                  toggleQuestionType(name);
                }}
                aria-hidden={false}
                checked={questionTypes.includes(name)}
              />
              <Text size='s' emphasis={fadeText ? 'lowest' : undefined}>
                {title}
              </Text>
              {sessionStore.isSuper() && countsLoaded && (
                <Text size='xs' emphasis={fadeText ? 'disabled' : 'lowest'}>
                  &nbsp;&nbsp;({typeCount})
                </Text>
              )}
            </Label>
          );
        })}
      </div>
    </div>
  );
};

const HidePreviouslyAssigned = () => {
  const {hidePreviouslyAssigned, setHidePreviouslyAssigned} = useContext(ContentDiscoveryContext);
  const {trackEvent} = useTracking({event: 'click', type: 'toggle'});

  return (
    <Label className='filter-container filter-label'>
      <Text size='s' className='u-flex-grow_1'>
        Hide Previously Assigned
      </Text>
      <Toggle
        className='u-mar-l_2'
        onClick={() => {
          setHidePreviouslyAssigned(!hidePreviouslyAssigned);
          trackEvent({content: 'hide previously assigned'});
        }}
        aria-hidden={false}
        checked={hidePreviouslyAssigned}
      />
    </Label>
  );
};

const HideAssessmentQuestions = () => {
  const {hideAssessmentQuestions, setHideAssessmentQuestions} = useContext(ContentDiscoveryContext);
  const {trackEvent} = useTracking({
    event: 'click',
    type: 'toggle'
  });

  return (
    <Label className='filter-container filter-label'>
      <Text size='s' className='u-flex-grow_1'>
        Hide Assessment Questions
      </Text>
      <Toggle
        className='u-mar-l_2'
        onClick={() => {
          setHideAssessmentQuestions(!hideAssessmentQuestions);
          trackEvent({content: 'hide assignment questions'});
        }}
        aria-hidden={false}
        checked={hideAssessmentQuestions}
      />
    </Label>
  );
};

export default SideBarSlider;
